import React, { useState, useEffect } from 'react';
import { 
  Bell, Package, Star, Info, Trash2, CheckCircle2, Circle, 
  ShoppingBag, MessageSquare, TrendingUp, Truck, DollarSign,
  Filter, Settings, Clock, AlertCircle, X, Eye, EyeOff,
  RefreshCw, Zap, Heart, Gift, Users, Award
} from 'lucide-react';
import { toast } from 'react-toastify';

const INITIAL_NOTIFICATIONS = [
  {
    id: "NOT001",
    type: "order",
    title: "New Order Received",
    message: "New order #ORD001 received. Please prepare it for shipping.",
    time: "2 hours ago",
    timestamp: Date.now() - 2 * 60 * 60 * 1000,
    read: false,
    priority: "high",
    actionLink: "/seller/orders"
  },
  {
    id: "NOT002",
    type: "review",
    title: "5-Star Review",
    message: "Rahul left a 5-star review on 'Aloe Vera Plant'.",
    time: "5 hours ago",
    timestamp: Date.now() - 5 * 60 * 60 * 1000,
    read: false,
    priority: "medium",
    actionLink: "/seller/reviews"
  },
  {
    id: "NOT003",
    type: "system",
    title: "Welcome to Seller Dashboard",
    message: "Welcome to Paudhawala! You can now start managing your nursery.",
    time: "1 day ago",
    timestamp: Date.now() - 24 * 60 * 60 * 1000,
    read: true,
    priority: "low",
    actionLink: null
  },
  {
    id: "NOT004",
    type: "order",
    title: "Order Delivered",
    message: "Order #ORD002 has been successfully delivered to the customer.",
    time: "2 days ago",
    timestamp: Date.now() - 48 * 60 * 60 * 1000,
    read: true,
    priority: "low",
    actionLink: "/seller/orders"
  },
  {
    id: "NOT005",
    type: "payout",
    title: "Payout Processed",
    message: "Your payout of ₹5,000 has been successfully processed.",
    time: "3 days ago",
    timestamp: Date.now() - 72 * 60 * 60 * 1000,
    read: false,
    priority: "high",
    actionLink: "/seller/earnings"
  }
];

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const localNotifs = JSON.parse(localStorage.getItem('sellerNotifications'));
    if (!localNotifs) {
      localStorage.setItem('sellerNotifications', JSON.stringify(INITIAL_NOTIFICATIONS));
      setNotifications(INITIAL_NOTIFICATIONS);
    } else {
      setNotifications(localNotifs);
    }
  }, []);

  const saveNotifications = (newNotifs) => {
    setNotifications(newNotifs);
    localStorage.setItem('sellerNotifications', JSON.stringify(newNotifs));
  };

  const toggleReadStatus = (id) => {
    const newNotifs = notifications.map(n => n.id === id ? { ...n, read: !n.read } : n);
    saveNotifications(newNotifs);
    toast.success("Notification updated.");
  };

  const deleteNotification = (id) => {
    const newNotifs = notifications.filter(n => n.id !== id);
    saveNotifications(newNotifs);
    toast.success("Notification deleted.");
  };

  const markAllAsRead = () => {
    const newNotifs = notifications.map(n => ({ ...n, read: true }));
    saveNotifications(newNotifs);
    toast.success("All notifications marked as read.");
  };

  const clearAll = () => {
    if (window.confirm("Are you sure you want to clear all notifications?")) {
      saveNotifications([]);
      toast.success("All notifications cleared.");
    }
  };

  const getStats = () => {
    const unreadCount = notifications.filter(n => !n.read).length;
    const orderCount = notifications.filter(n => n.type === 'order').length;
    const reviewCount = notifications.filter(n => n.type === 'review').length;
    return { unreadCount, orderCount, reviewCount };
  };

  const stats = getStats();

  const filteredNotifs = notifications
    .filter(n => filterType === 'all' || n.type === filterType)
    .sort((a, b) => b.timestamp - a.timestamp);

  const getIcon = (type, priority) => {
    const icons = {
      order: { icon: ShoppingBag, gradient: "linear-gradient(135deg, #3b82f6, #2563eb)", bg: "#dbeafe" },
      review: { icon: Star, gradient: "linear-gradient(135deg, #f59e0b, #d97706)", bg: "#fed7aa" },
      system: { icon: Info, gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)", bg: "#e9d5ff" },
      payout: { icon: DollarSign, gradient: "linear-gradient(135deg, #10b981, #059669)", bg: "#d1fae5" }
    };
    const selected = icons[type] || icons.system;
    const IconComponent = selected.icon;
    return <IconComponent size={20} color="white" />;
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      default: return '#10b981';
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card-notif">
      <div className="stat-icon-notif" style={{ backgroundColor: bgColor }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <h3 className="stat-value-notif">{value}</h3>
        <p className="stat-title-notif">{title}</p>
      </div>
    </div>
  );

  return (
    <div className="notifications-page">
      {/* Header */}
      <div className="notifications-header">
        <div className="header-left">
          <h1 className="page-title-notif">Notifications</h1>
          <p className="page-subtitle-notif">Stay updated with your orders and account activity</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh-notif" onClick={() => window.location.reload()}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="btn-clear-notif" onClick={clearAll}>
            <Trash2 size={16} />
            Clear All
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-notif-grid">
        <StatCard title="Total Notifications" value={notifications.length} icon={Bell} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Unread" value={stats.unreadCount} icon={AlertCircle} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Orders" value={stats.orderCount} icon={ShoppingBag} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Reviews" value={stats.reviewCount} icon={Star} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Filters Bar */}
      <div className="filters-notif-card">
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            <Bell size={14} />
            All
          </button>
          <button 
            className={`filter-btn ${filterType === 'order' ? 'active' : ''}`}
            onClick={() => setFilterType('order')}
          >
            <ShoppingBag size={14} />
            Orders
            {stats.orderCount > 0 && <span className="filter-count">{stats.orderCount}</span>}
          </button>
          <button 
            className={`filter-btn ${filterType === 'review' ? 'active' : ''}`}
            onClick={() => setFilterType('review')}
          >
            <Star size={14} />
            Reviews
          </button>
          <button 
            className={`filter-btn ${filterType === 'system' ? 'active' : ''}`}
            onClick={() => setFilterType('system')}
          >
            <Info size={14} />
            System
          </button>
          <button 
            className={`filter-btn ${filterType === 'payout' ? 'active' : ''}`}
            onClick={() => setFilterType('payout')}
          >
            <DollarSign size={14} />
            Payouts
          </button>
        </div>
        
        <div className="filter-actions">
          <button className="mark-all-btn" onClick={markAllAsRead}>
            <CheckCircle2 size={14} />
            Mark all as read
          </button>
        </div>
      </div>

      {/* Notifications List */}
      {filteredNotifs.length === 0 ? (
        <div className="empty-state-notif">
          <div className="empty-icon-wrapper">
            <Bell size={64} />
          </div>
          <h3>All caught up!</h3>
          <p>You don't have any {filterType !== 'all' ? filterType : ''} notifications right now.</p>
        </div>
      ) : (
        <div className="notifications-list">
          {filteredNotifs.map((notif) => (
            <div key={notif.id} className={`notification-card ${!notif.read ? 'unread' : ''}`}>
              <div className="notification-icon" style={{ background: getIcon(notif.type, notif.priority).props.children ? '' : '' }}>
                <div className="icon-wrapper" style={{ background: notif.type === 'order' ? '#3b82f6' : notif.type === 'review' ? '#f59e0b' : notif.type === 'payout' ? '#10b981' : '#8b5cf6' }}>
                  {getIcon(notif.type, notif.priority)}
                </div>
              </div>
              
              <div className="notification-content">
                <div className="notification-header">
                  <div className="notification-title-group">
                    <h6 className={`notification-title ${!notif.read ? 'bold' : ''}`}>
                      {notif.title}
                    </h6>
                    {!notif.read && <span className="unread-badge">New</span>}
                    {notif.priority === 'high' && !notif.read && (
                      <span className="priority-badge high">High Priority</span>
                    )}
                  </div>
                  <div className="notification-time">
                    <Clock size={12} />
                    <span>{notif.time}</span>
                  </div>
                </div>
                
                <p className={`notification-message ${!notif.read ? 'active' : ''}`}>
                  {notif.message}
                </p>
                
                <div className="notification-actions">
                  {notif.actionLink && (
                    <a href={notif.actionLink} className="action-link">
                      View Details
                      <ArrowRight size={12} />
                    </a>
                  )}
                  <button 
                    className="action-btn mark-btn" 
                    onClick={() => toggleReadStatus(notif.id)}
                  >
                    {notif.read ? <EyeOff size={14} /> : <Eye size={14} />}
                    {notif.read ? 'Mark unread' : 'Mark read'}
                  </button>
                  <button 
                    className="action-btn delete-btn" 
                    onClick={() => deleteNotification(notif.id)}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`.notifications-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.notifications-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title-notif {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4px;
}

.page-subtitle-notif {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-refresh-notif,
.btn-clear-notif {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh-notif {
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.btn-refresh-notif:hover {
  border-color: #10b981;
  color: #10b981;
}

.btn-clear-notif {
  background: white;
  border: 1px solid #fee2e2;
  color: #ef4444;
}

.btn-clear-notif:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

.stats-notif-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.stat-card-notif {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
  transition: all 0.3s ease;
}

.stat-card-notif:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

.stat-icon-notif {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
}

.stat-value-notif {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.stat-title-notif {
  font-size: 12px;
  color: #64748b;
  margin: 4px 0 0;
}

.filters-notif-card {
  background: white;
  border-radius: 20px;
  padding: 12px 20px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.filter-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #f1f5f9;
  border: none;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.filter-btn.active {
  background: #10b981;
  color: white;
}

.filter-btn:hover:not(.active) {
  background: #e2e8f0;
  color: #10b981;
}

.filter-count {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 20px;
}

.mark-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: #f1f5f9;
  border: none;
  border-radius: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mark-all-btn:hover {
  background: #d1fae5;
  color: #10b981;
}

.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.notification-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid #e8ecef;
  position: relative;
  overflow: hidden;
}

.notification-card.unread {
  background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
  border-left: 4px solid #10b981;
}

.notification-card:hover {
  transform: translateX(4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}

.icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.notification-icon {
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.notification-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.notification-title {
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
  margin: 0;
}

.notification-title.bold {
  font-weight: 700;
}

.unread-badge {
  padding: 2px 10px;
  background: #10b981;
  color: white;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.priority-badge {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.notification-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.notification-message {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 16px;
  line-height: 1.5;
}

.notification-message.active {
  color: #1e293b;
}

.notification-actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.action-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #10b981;
  text-decoration: none;
  transition: all 0.3s ease;
}

.action-link:hover {
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.mark-btn {
  color: #64748b;
}

.mark-btn:hover {
  background: #f1f5f9;
  color: #10b981;
}

.delete-btn {
  color: #ef4444;
}

.delete-btn:hover {
  background: #fee2e2;
}

.empty-state-notif {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 24px;
}

.arrow-right {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-right: 2px solid currentColor;
  border-top: 2px solid currentColor;
  transform: rotate(45deg);
  margin-left: 4px;
}

@media (max-width: 768px) {
  .notifications-page {
    padding: 16px;
  }

  .stats-notif-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters-notif-card {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-buttons {
    justify-content: center;
  }

  .notification-card {
    flex-direction: column;
  }

  .notification-header {
    flex-direction: column;
  }

  .notification-actions {
    flex-wrap: wrap;
  }

  .header-actions {
    width: 100%;
  }

  .btn-refresh-notif,
  .btn-clear-notif {
    flex: 1;
    justify-content: center;
  }
}`}</style>
    </div>
  );
};

// ArrowRight component
const ArrowRight = ({ size }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

export default Notifications;